import "./Documents.scss";

import clsx from "clsx";
import { useAtom } from "jotai";
import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router-dom";

import DocumentMint from "@/assets/ux/DocumentMint.svg?react";
import { Tablist } from "@/components/Interface/Tablist/Tablist";
import { PortalHeading } from "@/components/Portal/components/PortalHeading/PortalHeading";
import { useIsMobile } from "@/hooks/useIsMobile";
import { usePaymentsTabLabel } from "@/hooks/usePaymentsTabLabel";
import {
  documentsEditStateAtom,
  documentsEditStateAtomInitialState,
} from "@/utils/atoms";

/**
 * Rechnungen section: an icon header, the three document tabs (Rechnungen,
 * Verbindungsübersicht, Zahlungsmittel) and their routed content. Layout and
 * spacing follow TabLayout's Figma rhythm; chrome is hidden in-place during
 * mobile multi-select so the routed outlet never remounts.
 */
export const Documents: FC = () => {
  const { t } = useTranslation("portal");
  const isMobile = useIsMobile();
  const [documentsEdit, setDocumentsEdit] = useAtom(documentsEditStateAtom);
  const paymentsTabLabel = usePaymentsTabLabel();

  useEffect(() => {
    return () => {
      setDocumentsEdit(documentsEditStateAtomInitialState);
    };
  }, [setDocumentsEdit]);

  const isStandaloneMobileFlow =
    isMobile &&
    (documentsEdit.billsSelecting || documentsEdit.itemizedBillsSelecting);

  return (
    <div className="flex w-full flex-col">
      {!isStandaloneMobileFlow && (
        <>
          <div className="tablet:mx-3">
            <PortalHeading
              heading={t("documents.heading")}
              icon={<DocumentMint />}
            />
          </div>
          <div className="mt-gutter tablet:mx-4">
            <Tablist
              tabs={[
                {
                  target: "bills",
                  disabled: false,
                  text: t("bills.heading"),
                },
                {
                  target: "itemized-bills",
                  disabled: false,
                  text: t("documents.tabItemizedBill.title"),
                },
                {
                  target: "payments",
                  disabled: false,
                  text: paymentsTabLabel,
                },
              ]}
            />
          </div>
        </>
      )}
      {/* Keep a stable wrapper around <Outlet /> — toggling standalone mode
          must not remount BillList, or local selection state resets on click.

          Gutter / horizontal inset live on this outer shell so they are not
          cancelled by `#documents-container { margin-top: 0 !important }`. */}
      <div
        className={clsx(
          "min-w-0",
          isStandaloneMobileFlow
            ? "documents-container--standalone-edit"
            : "mt-gutter min-h-[560px] tablet:mx-6",
        )}
      >
        <div id="documents-container">
          <Outlet />
        </div>
      </div>
    </div>
  );
};
